import { useMemo } from "react";

import { useGetUpcomingShiftsGrouped } from "../../api/useGetUpcomingShiftsGrouped";
import { type ChatChannel, type ChatChannelCustomTypesFilter } from "../../types";
import { enrichAndSortChannels } from "../../utils/enrichChannels";
import { useFilterChannelsByWorkplaceStatus } from "./useFilterChannelsByWorkplaceStatus";
import { useGetChatChannels } from "./useGetChatChannels";

interface UseChatChannelListParams {
  searchInput?: string;
  customTypesFilter?: ChatChannelCustomTypesFilter;
  enabled?: boolean;
  /**
   * When true, skips the workplace-status filter that removes channels
   * for SUSPENDED / TERMINATED / ARCHIVED workplaces. Useful for the
   * detail page where the user has already navigated to a specific channel.
   */
  skipWorkplaceFilter?: boolean;
}

interface UseChatChannelListResult {
  channels: ChatChannel[];
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => Promise<unknown>;
}

/**
 * Unified hook for the chat channel list. Handles fetching, enrichment,
 * and workplace-status filtering in a single pipeline — regardless of
 * whether the user is searching/filtering or viewing the default list.
 *
 * This replaces the previous dual-pipeline pattern where the default list
 * came from useChatStateContext and search/filter results were enriched
 * locally in ChatListPage.
 */
export function useChatChannelList(
  params: UseChatChannelListParams = {}
): UseChatChannelListResult {
  const { searchInput, customTypesFilter, enabled = true, skipWorkplaceFilter = false } = params;

  const channelsQuery = useGetChatChannels({
    searchInput,
    customTypesFilter,
    enabled,
  });

  const rawChannels = channelsQuery.data?.channels;

  const shiftsQuery = useGetUpcomingShiftsGrouped({
    enabled,
  });

  const shiftsByFacility = shiftsQuery.data?.response;

  const enrichedChannels = useMemo(
    () => enrichAndSortChannels(rawChannels ?? [], shiftsByFacility),
    [rawChannels, shiftsByFacility]
  );

  // Filter out channels for workplaces with inactive statuses (SUSPENDED, TERMINATED, ARCHIVED)
  const {
    filteredChannels,
    isLoading: isLoadingWorkplaceStatuses,
    isFetching: isFetchingWorkplaceStatuses,
    refetch: refetchWorkplaceStatuses,
  } = useFilterChannelsByWorkplaceStatus({
    channels: enrichedChannels,
    enabled: enabled && !skipWorkplaceFilter,
  });

  async function refetch(): Promise<void> {
    await Promise.all([channelsQuery.refetch(), shiftsQuery.refetch(), refetchWorkplaceStatuses()]);
  }

  const isLoading =
    // The useGetChatChannels hook returns isSdkInitialized: false until the SDK is initialized, but it still returns an empty array of channels and behaves as if it had loaded successfully.
    // This is a workaround to prevent the empty state from showing until the SDK is initialized.
    // The ideal fix would be to disable the query until the SDK is initialized, but the hook has multiple callers that would need to be verified/updated for the new behavior.
    // Leaving this for now, but should be refactored in the future.
    !channelsQuery.data?.isSdkInitialized ||
    channelsQuery.isLoading ||
    shiftsQuery.isLoading ||
    (!skipWorkplaceFilter && isLoadingWorkplaceStatuses);

  return {
    channels: skipWorkplaceFilter ? enrichedChannels : filteredChannels,
    isLoading,
    isFetching: channelsQuery.isFetching || shiftsQuery.isFetching || isFetchingWorkplaceStatuses,
    refetch,
  };
}
